import { useCallback, useMemo } from 'react'
import { ReadSyncValidationsDocument } from 'generated/resources/graphql'
import { print } from 'graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { toSyncParameters } from 'modules/integrations/lib/parameters'
import {
  BlockingIssue,
  useCompensationsBlockingIssues,
} from 'modules/integrations/hooks/SyncContext/blockingIssues'

interface UseBlockingIssuesLazyQueryResult {
  fetchBlockingIssues: () => Promise<void>
  blockingIssues: BlockingIssue[]
  blockingIssuesLoading: boolean
  anyValidSyncable: boolean
}

export const useBlockingIssuesLazyQuery = (): UseBlockingIssuesLazyQueryResult => {
  const {
    syncEnabled,
    marketplaceIntegration,
    contextParameters,
    getDataCollectionSource,
    configuration,
    isBookkeeperView,
    companyId,
  } = usePrivateIntegrationsSyncContext()
  const [
    fetchSyncValidationsQuery,
    { data: blockingIssuesData, loading: blockingIssuesLoading, networkActivity },
  ] = useLazyQuery(ReadSyncValidationsDocument)

  const validationNode =
    blockingIssuesData?.integrations?.syncValidationsConnection.nodes[0]
  const issues = useMemo(() => validationNode?.issues ?? [], [validationNode])
  const totalSyncables = validationNode?.totalSyncables ?? 0
  const invalidSyncables = validationNode?.invalidSyncables ?? 0
  const anyValidSyncable = invalidSyncables < totalSyncables

  const fetchBlockingIssues = useCallback(async () => {
    if (!syncEnabled || !marketplaceIntegration || !companyId) return

    const dataCollectionSource = getDataCollectionSource()
    const mappedVariables = dataCollectionSource?.mappedVariables
    const queryForSync = dataCollectionSource?.document
      ? print(dataCollectionSource.document)
      : null
    const graphqlVariablesJson =
      queryForSync && mappedVariables
        ? JSON.stringify({
            query: queryForSync,
            variables: { ...mappedVariables },
          })
        : undefined

    await fetchSyncValidationsQuery({
      variables: {
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        parameters: toSyncParameters(contextParameters),
        graphqlVariablesJson,
      },
      context: {
        isBookkeeperView,
      },
    })
  }, [
    syncEnabled,
    marketplaceIntegration,
    companyId,
    getDataCollectionSource,
    configuration.origin,
    contextParameters,
    isBookkeeperView,
    fetchSyncValidationsQuery,
  ])

  return {
    fetchBlockingIssues,
    blockingIssues: useCompensationsBlockingIssues(issues, marketplaceIntegration),
    blockingIssuesLoading: networkActivity || blockingIssuesLoading,
    anyValidSyncable,
  }
}
